import { Paper } from "@mantine/core";
import { productApi } from "apis/productApi";
import { Loading } from "components";
import { ErrorStatusType } from "constants/types/common";
import { ProductRequestBodyType } from "constants/types/product";
import { useFormik } from "formik";
import { FORM_IDS } from "pages/Product/config";
import { uploadProductImage } from "pages/Product/config";
import { FC, useRef, useState } from "react";
import { useNotification } from "utils/hooks";
import { useListMeasurementUnit } from "utils/hooks/request/useMeasurementUnits";
import { useListProductCategory } from "utils/hooks/request/useProductCategories";
import ProductFormFields from "../ProductFormFields/ProductFormFields";
import { createValidationSchema } from "./config";

interface CreateFormProps {
  onClose?: () => void;
  mutate?: () => void;
}

const CreateForm: FC<CreateFormProps> = ({ onClose, mutate }) => {
  const { notification } = useNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  // Giữ key sau upload thành công để tránh upload lại khi retry
  const uploadedImageKeyRef = useRef<string | null>(null);

  const {
    measurementOptions,
    mutate: measurementMutate,
  } = useListMeasurementUnit();

  const {
    productCategoryOptions,
    mutate: categoryMutate,
  } = useListProductCategory();

  const handleImageFileChange = (file: File | null) => {
    setSelectedImageFile(file);
    if (!file) uploadedImageKeyRef.current = null;
  };

  const formik = useFormik<ProductRequestBodyType>({
    initialValues: {
      code: "",
      name: "",
      spec: "",
      unitId: "",
      priceNet: 0,
      categoryId: "",
      isActive: true,
      notes: "",
    },
    validationSchema: createValidationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        let imageKey: string | null = uploadedImageKeyRef.current;
        if (selectedImageFile && !imageKey) {
          try {
            imageKey = await uploadProductImage(selectedImageFile);
            uploadedImageKeyRef.current = imageKey;
          } catch {
            notification.error({ title: "Upload ảnh thất bại, vui lòng thử lại" });
            setIsSubmitting(false);
            return;
          }
        }

        const res = await productApi.create({ ...values, imageKey });
        if (res.data.success) {
          notification.success({ title: "Tạo mặt hàng thành công" });
          onClose?.();
          mutate?.();
          formik.resetForm();
          setSelectedImageFile(null);
          uploadedImageKeyRef.current = null;
        }
      } catch (error: ErrorStatusType | any) {
        notification.error({
          title: "Tạo mặt hàng thất bại",
          message: error?.error?.message,
        });
      }
      setIsSubmitting(false);
    },
  });

  return (
    <Loading variant="overlay" visible={isSubmitting}>
      <Paper>
        <form id={FORM_IDS.CREATE} onSubmit={formik.handleSubmit}>
          <ProductFormFields
            formik={formik}
            measurementOptions={measurementOptions ?? []}
            productCategoryOptions={productCategoryOptions ?? []}
            onMeasurementMutate={measurementMutate}
            onCategoryMutate={categoryMutate}
            selectedImageFile={selectedImageFile}
            onImageFileChange={handleImageFileChange}
          />
        </form>
      </Paper>
    </Loading>
  );
};

export default CreateForm;
